/*
 * Copyright (c) CIRROM 2026.
 */

import { API } from "./api";

export interface AccountSummaryCrmRecord {
  id: number;
  crm_account_id: string;
  firm_global_id: string | null;
  company: string | null;
  telephone: string | null;
  phone_id: string | null;
  website: string | null;
  country: string | null;
  city: string | null;
  stateorprovince: string | null;
  address_id: string | null;
  contact_manager: string | null;
  created_time: string | null;
  company_type: string | null;
  company_status: string | null;
  external_identifier: string | null;
  num_outreach: number | null;
  num_pipeline: number | null;
  account_source: string | null;
  last_update_batch_id: number | null;
  last_seen_batch_id: number | null;
}

export interface AccountSummaryCompareField {
  field_config_id: number;
  field_name: string;
  crm_field_label: string | null;
  /** Joins this configured field to a reference-record field's `label`. */
  data_source_field_label: string | null;
  /** null on UI-only fields, which have no CRM counterpart. */
  crm_value: string | null;
  /** The enriched value, read live from the linked reference record. */
  data_source_value: string | null;
  /** null on UI-only fields; true/false when both sides exist. */
  is_matching: boolean | null;
  field_source_type: string;
}

/**
 * One field the source holds on the linked reference record - the complete
 * inventory, not just what the project maps. `is_mapped` says whether an
 * enrich field config consumes it.
 */
export interface AccountSummaryReferenceField {
  field_name: string;
  label: string;
  value: string | null;
  is_mapped: boolean;
}

export interface AccountSummaryFieldCompare {
  matching_id: number;
  account_name: string | null;
  account_crm_id: string;
  crm_record: { crm_account_url: string | null };
  reference_record: {
    source_record_url: string | null;
    /** All fields the source holds; absent on older backend versions. */
    fields?: AccountSummaryReferenceField[];
  };
  fields: AccountSummaryCompareField[];
}

/*
 * Same row shape as EnrichLinkageResult in enrich.ts. Kept separate because this
 * payload documents its own nullability: on a matched row `proposal_id` is the
 * matching record's id rather than a pending proposal's.
 */
export interface AccountSummaryLinkageRow {
  proposal_id: number;
  search_type: string | null;
  company_name: string | null;
  datasource_id: string | null;
  /**
   * Matched rows only; null when the reference record no longer exists.
   * Absent on proposals, which are compared on the linkage surface instead.
   */
  field_compare?: AccountSummaryFieldCompare | null;
}

/** One entry per enrich sync group of the integrated system. */
export interface AccountSummaryEnrichSystem {
  sync_group_id: number;
  /** Representative active project containing this account; null outside all active project scopes. */
  data_project_id: number | null;
  enrich_system_id: number;
  enrich_system_name: string;
  enrich_system_type: string;
  in_scope: boolean;
  linkage_proposals: AccountSummaryLinkageRow[];
  linkage_matched: AccountSummaryLinkageRow[];
}

/*
 * `configured: false` arrives with no other keys; `systems` is only present
 * when the project has at least one enrich sync group set up.
 */
export interface AccountSummaryEnrich {
  configured: boolean;
  systems?: AccountSummaryEnrichSystem[];
}

export interface AccountSummary {
  system_id: number;
  crm_account_id: string;
  crm: AccountSummaryCrmRecord;
  /** Tenant-configured column names to values; {} when none exist. */
  additional_attributes: Record<string, string | null>;
  enrich: AccountSummaryEnrich;
}

/**
 * Aggregated view of everything known about a single CRM account across all
 * enrich sync groups of the integrated system. 404s when the system or
 * account id is unknown.
 */
export const getAccountSummary = async (
  integratedSystemId: number,
  crmAccountId: string
): Promise<AccountSummary> => {
  const response = await API.get("account-summary", {
    params: { system_id: integratedSystemId, crm_account_id: crmAccountId },
  });
  return response.data;
};

/*
 * `crm_account_id` is null on accounts the CRM never gave an id, which are the
 * ones with no summary page to open.
 */
export interface AccountSearchResult {
  id: number;
  company: string;
  website: string | null;
  crm_account_id: string | null;
}

/** Substring match on company name, capped at 100 rows by the backend. */
export const searchForAccounts = async (
  integratedSystemId: number,
  searchTerm: string
): Promise<AccountSearchResult[]> => {
  const response = await API.get("/search-for-accounts", {
    params: {
      integrated_system_id: integratedSystemId,
      search_term: searchTerm,
    },
  });
  return response.data;
};
